import { memo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { clearMessages } from 'store/ancillary/actions';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Typography from '@mui/material/Typography';
import { getTrainingTracks } from '../_api';
import { getSystemName } from '../../../Utilities/EpicAdministration/_utils';

const JobCategoryDetails = memo(function JobCategoryDetails({ jobCategory }) {
	const dispatch = useDispatch();
	const [sShowTrainingTracks, setShowTrainingTracks] = useState(false);
	const [sArrTrainingTracks, setArrTrainingTracks] = useState([]);

	const onExpandTrackHandler = async expand => {
		setShowTrainingTracks(expand);
		// if tracks requested for first time
		if (
			expand &&
			sArrTrainingTracks.length < 1 &&
			jobCategory.trainingTracks.length > 0
		) {
			try {
				const arrTrainingTracks = await getTrainingTracks(
					jobCategory.trainingTracks
				);

				setArrTrainingTracks(arrTrainingTracks);
			} catch (ex) {
				setShowTrainingTracks(false);
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			}
		}
	};

	return (
		<Stack
			sx={{ marginTop: '32px' }}
			direction='column'
			spacing={{
				xs: 0.5,
				sm: 0.5,
				md: 1,
			}}
		>
			{jobCategory?.note && (
				<>
					<Typography
						sx={{ fontStyle: 'italic', color: 'info.main' }}
						variant='body2'
					>
						<ReactMarkdown
							components={{
								a: props => (
									<a
										href={props.href}
										target='_blank'
										rel='noreferrer'
									>
										{props.children}
									</a>
								),
							}}
						>
							{'Note: ' + jobCategory.note}
						</ReactMarkdown>
					</Typography>
					<Divider sx={{ opacity: '0' }} orientation='horizontal' />
				</>
			)}
			<Typography variant='body2'>
				<b>Name: </b>
				{jobCategory.name}
			</Typography>
			<Divider orientation='horizontal' />
			<Typography variant='body2'>
				<b>Description: </b>
				{jobCategory.description}
			</Typography>
			<Divider orientation='horizontal' />
			<Typography variant='body2'>
				<b>Type: </b>
				{jobCategory.type}
			</Typography>
			<Divider orientation='horizontal' />
			<Typography variant='body2'>
				<b>Template Name: </b>
				{jobCategory.templateName}
			</Typography>
			<Divider orientation='horizontal' />
			<Typography variant='body2'>
				<b>Day in the Life Activity, Pre-Live: </b>
				{jobCategory.dayInLifeActivityPre}
			</Typography>
			<Divider orientation='horizontal' />
			<Typography variant='body2'>
				<b>Day in the Life Activity, Post-Live: </b>
				{jobCategory.dayInLifeActivityPost}
			</Typography>
			<Divider orientation='horizontal' />
			<>
				<Box
					display='flex'
					justifyContent='space-between'
					alignItems='center'
				>
					<Typography variant='body2'>
						<b>Training Classes: </b>
						{jobCategory.trainingTracks.length < 1 &&
							`No ${
								getSystemName(jobCategory.appID)
							} training required`}
					</Typography>
					{jobCategory.trainingTracks.length > 0 && (
						<IconButton
							onClick={() =>
								onExpandTrackHandler(!sShowTrainingTracks)
							}
						>
							{sShowTrainingTracks ? (
								<ExpandLessIcon />
							) : (
								<ExpandMoreIcon />
							)}
						</IconButton>
					)}
				</Box>
				{sShowTrainingTracks &&
					(sArrTrainingTracks.length > 0 ? (
						sArrTrainingTracks.map((trainingTrack, index) => (
							<Typography
								key={trainingTrack?.id ?? index}
								variant='body2'
							>
								{index + 1}. {trainingTrack?.name ?? 'N/A'}
							</Typography>
						))
					) : (
						<>
							{jobCategory.trainingTracks.map((_, index) => (
								<Skeleton
									key={index}
									sx={{ fontSize: '14px' }}
									animation='pulse'
									variant='text'
									width='50%'
								/>
							))}
						</>
					))}
			</>
		</Stack>
	);
});

export { JobCategoryDetails };
